import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ChevronDown, Loader2 } from "lucide-react";
import { HOME } from "@/constants/testIds";
import { HALL_OPTIONS, IMAGES, PHONE_DISPLAY, PHONE_TEL, WA_URL } from "@/constants/neco";
import { trackWhatsAppClick } from "@/lib/gtag";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full bg-black/20 border border-[#D4AF37]/20 focus:border-[#D4AF37] outline-none px-4 py-3.5 text-sm text-[#F5F5F0] placeholder:text-[#B3B0A8]/60 transition-colors duration-300";

const Field = ({ label, children, testId }) => (
  <label className="flex flex-col gap-2" data-testid={testId}>
    <span className="text-[10px] uppercase tracking-[0.25em] text-[#B3B0A8]">{label}</span>
    {children}
  </label>
);

export const Reservation = () => {
  const [form, setForm] = useState({
    name: "",
    hall: "",
    phone: "",
    date_range: "",
    requests: "",
    email: "",
    kvkk: false,
  });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) =>
    setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.kvkk) {
      toast.error("Devam etmek için KVKK metnini onaylayın.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/reservations`, {
        name: form.name,
        phone: form.phone,
        email: form.email || null,
        hall: form.hall,
        date_range: form.date_range || null,
        requests: form.requests || null,
        kvkk_accepted: form.kvkk,
      });
      setDone(true);
      toast.success("Rezervasyon talebiniz alındı. En kısa sürede sizi arayacağız.");
    } catch (err) {
      toast.error("Talebiniz gönderilemedi. Lütfen telefonla bize ulaşın.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="rezervasyon" data-testid={HOME.reservationSection} className="relative py-24 md:py-32 overflow-hidden border-y border-[#D4AF37]/10">
      <div className="absolute inset-0">
        <img src={IMAGES.cake} alt="" aria-hidden="true" className="w-full h-full object-cover opacity-10" loading="lazy" />
        <div className="absolute inset-0 bg-[#121110]/90" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5">
          <p className="text-xs uppercase tracking-[0.35em] text-[#D4AF37]">Online Rezervasyon</p>
          <h2 className="font-display text-4xl sm:text-5xl tracking-tight text-[#F5F5F0] mt-5 gold-rule">
            Tarihinizi Seçin, <span className="font-romance text-[#E5C783]">Sizi Arayalım</span>
          </h2>
          <p className="mt-12 text-base font-light leading-relaxed text-[#B3B0A8]">
            Organizasyonunuzu ve tarih aralığını seçin; sizi arayalım, salon ve hizmetlerimiz
            hakkında bilgilendirelim.
          </p>
          <div className="mt-10 space-y-4 text-sm">
            <p className="text-[#B3B0A8]">
              Telefonla rezervasyon için:{" "}
              <a href={PHONE_TEL} className="text-[#E5C783] hover:text-[#D4AF37] transition-colors duration-300">
                {PHONE_DISPLAY}
              </a>
            </p>
            <p className="text-[#B3B0A8]">
              WhatsApp için:{" "}
              <a href={WA_URL} target="_blank" rel="noopener noreferrer" onClick={trackWhatsAppClick} className="text-[#E5C783] hover:text-[#D4AF37] transition-colors duration-300">
                0532 475 64 61
              </a>
            </p>
            <p className="text-xs text-[#B3B0A8]/70">Çalışma saatlerimiz hafta içi 09.00 – 19.00 arasındadır.</p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid={HOME.reservationSuccess} className="bg-[#1A1817]/90 border border-[#D4AF37]/30 p-10 md:p-14 text-center">
              <p className="font-display text-3xl text-[#E5C783]">Teşekkürler</p>
              <p className="text-sm font-light text-[#B3B0A8] mt-4 leading-relaxed">
                Rezervasyon talebiniz bize ulaştı. Salon ve hizmetlerimiz hakkında bilgilendirmek
                için en kısa sürede sizi arayacağız.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-8 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#121110] px-8 py-3 text-[11px] uppercase tracking-[0.25em] transition-colors duration-300"
              >
                Yeni Talep Oluştur
              </button>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-testid={HOME.reservationForm}
              className="bg-[#1A1817]/90 backdrop-blur-md border border-[#D4AF37]/20 p-8 md:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <Field label="Ad Soyad *" testId={HOME.reservationNameInput}>
                  <input required value={form.name} onChange={set("name")} className={inputCls} placeholder="Adınız Soyadınız" />
                </Field>
                <Field label="Salon Seçiniz *" testId={HOME.reservationHallSelect}>
                  <div className="relative">
                    <select required value={form.hall} onChange={set("hall")} className={`${inputCls} appearance-none cursor-pointer`}>
                      <option value="" disabled className="bg-[#1A1817]">Bir salon seçiniz</option>
                      {HALL_OPTIONS.map((h) => (
                        <option key={h} value={h} className="bg-[#1A1817]">{h}</option>
                      ))}
                    </select>
                    <ChevronDown size={14} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#B3B0A8] pointer-events-none" />
                  </div>
                </Field>
                <Field label="Telefon *" testId={HOME.reservationPhoneInput}>
                  <input required type="tel" value={form.phone} onChange={set("phone")} className={inputCls} placeholder="05xx xxx xx xx" />
                </Field>
                <Field label="Tarih Aralığı" testId={HOME.reservationDateInput}>
                  <input value={form.date_range} onChange={set("date_range")} className={inputCls} placeholder="Örn: Haziran 2026" />
                </Field>
                <div className="sm:col-span-2">
                  <Field label="İstekleriniz" testId={HOME.reservationRequestsInput}>
                    <textarea rows={3} value={form.requests} onChange={set("requests")} className={`${inputCls} resize-none`} placeholder="Organizasyonunuz hakkında kısaca bilgi verin" />
                  </Field>
                </div>
                <div className="sm:col-span-2">
                  <Field label="E-posta" testId={HOME.reservationEmailInput}>
                    <input type="email" value={form.email} onChange={set("email")} className={inputCls} placeholder="ornek@eposta.com" />
                  </Field>
                </div>
              </div>

              <label className="flex items-start gap-3 mt-7 cursor-pointer" data-testid={HOME.reservationKvkkCheckbox}>
                <input
                  type="checkbox"
                  checked={form.kvkk}
                  onChange={set("kvkk")}
                  className="mt-1 h-4 w-4 shrink-0 appearance-none border border-[#D4AF37]/40 bg-black/20 checked:bg-[#D4AF37] transition-colors duration-200 cursor-pointer"
                />
                <span className="text-xs font-light leading-relaxed text-[#B3B0A8]">
                  Kişisel Verilerin Korunmasına ilişkin Bilgilendirme ve Açık Rıza Metnini okudum,
                  kabul ediyorum.
                </span>
              </label>

              <button
                type="submit"
                disabled={sending}
                data-testid={HOME.reservationSubmitButton}
                className="mt-8 w-full bg-[#D4AF37] hover:bg-[#E5C783] disabled:opacity-60 text-[#121110] py-4 text-xs uppercase tracking-[0.3em] font-semibold transition-colors duration-300 flex items-center justify-center gap-2"
              >
                {sending && <Loader2 size={15} className="animate-spin" />}
                Gönder
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
};
